import { Table, Text } from "@mantine/core"
import { IconEyeOff } from "@tabler/icons-react"
import { StatusBadge } from "../../../../../-components/status-badge"
import { getFormattedTime } from "../../../../../../../utils/get-formatted-time"
import { useTranslation } from "react-i18next"
import { PersonLink } from "./person-links"
import { RoleLink } from "./role-link"
import { useSuspenseQueryGet } from "../../../../../../../typed-client/hooks/use-suspense-query-get"
import { useTimePeriod } from "../../../../../../../utils/use-time-period"
import { TheasoftProduct_AllValues } from "../../../../../../../@client/Theasoft_DataClasses_Security"
import { OwnerRoleCastTableRows } from "@/@client/Theasoft_ApiServer_Roles"
import { useGetMyLicenses } from "@/routes/app/-components/use-get-my-licenses"

interface Props {
  activityId: number
  ownerGroups: string[]
}

export function CastsTable({ activityId, ownerGroups }: Props) {
  const { t } = useTranslation("app_server")
  const { data: productionCasts } = useSuspenseQueryGet(
    "/api/Role/GetRoleCastTable",
    {
      activityId,
      ownerGroups,
    }
  )
  const { data: licenses } = useGetMyLicenses()

  const isManager = licenses?.some(
    (license) =>
      license.product === TheasoftProduct_AllValues[22] ||
      license.product === TheasoftProduct_AllValues[25]
  )

  return (
    <Table styles={{ td: { width: "fit-content" } }} stickyHeader>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>{t("Role")}</Table.Th>
          <Table.Th>{t("Person")}</Table.Th>
          {isManager ? (
            <>
              <Table.Th>{t("From")}</Table.Th>
              <Table.Th>{t("MaskTimeBefore")}</Table.Th>
              <Table.Th>{t("To_lower")}</Table.Th>
              <Table.Th>{t("MaskTimeAfter")}</Table.Th>
              <Table.Th>{t("Status")}</Table.Th>
              <Table.Th>{t("Secret")}</Table.Th>
              <Table.Th>{t("Note")}</Table.Th>
              <Table.Th>{t("From")}</Table.Th>
              <Table.Th>{t("To")}</Table.Th>
            </>
          ) : null}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {productionCasts.map((production, i) => (
          <React.Fragment key={i}>
            {production.title && (
              <Table.Tr style={{ borderBottom: "none" }}>
                <Table.Td>
                  <Text fz={"xl"} fw={"bold"}>
                    {production.title}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            <TableFragment
              ownerRoles={production.ownerRoles}
              isManager={isManager}
            />
          </React.Fragment>
        ))}
      </Table.Tbody>
    </Table>
  )
}

function TableFragment({
  ownerRoles,
  isManager,
}: {
  ownerRoles: OwnerRoleCastTableRows[]
  isManager: boolean
}) {
  const { getTimePeriod } = useTimePeriod()

  return (
    <>
      {ownerRoles.map((group) => (
        <React.Fragment key={group.owner}>
          <Table.Tr style={{ borderBottom: "none" }}>
            <Table.Td>
              <Text fw={500} fz={"lg"}>
                {group.owner}
              </Text>
            </Table.Td>
          </Table.Tr>
          {group.rows.map((roleCasts, i) => (
            <React.Fragment key={i}>
              {roleCasts.rows.map((roleCast, index, array) => (
                <Table.Tr
                  style={
                    array.length > 1 && index < array.length - 1
                      ? { borderBottom: "none" }
                      : undefined
                  }
                  key={i}
                >
                  <Table.Td>
                    {index === 0 && (
                      <RoleLink
                        productionId={roleCast.productionId}
                        roleId={roleCast.roleId}
                        name={roleCast.role}
                      />
                    )}
                  </Table.Td>
                  <Table.Td>
                    {roleCast.personId ? (
                      <Text>
                        <PersonLink personId={roleCast.personId} />
                        {!isManager && (roleCast.startTime || roleCast.endTime)
                          ? ` (${getTimePeriod({ start: roleCast.startTime, end: roleCast.endTime })})`
                          : ""}
                      </Text>
                    ) : (
                      ""
                    )}
                  </Table.Td>
                  {isManager ? (
                    <>
                      <Table.Td>
                        {getFormattedTime(roleCast.startTime)}
                      </Table.Td>
                      <Table.Td>
                        {getFormattedTime(roleCast.maskTimeBefore)}
                      </Table.Td>
                      <Table.Td>{getFormattedTime(roleCast.endTime)}</Table.Td>
                      <Table.Td>
                        {getFormattedTime(roleCast.maskTimeAfter)}
                      </Table.Td>
                      <Table.Td>
                        <StatusBadge status={roleCast.status} />
                      </Table.Td>
                      <Table.Td>{roleCast.isSecret && <IconEyeOff />}</Table.Td>
                      <Table.Td>{roleCast.note}</Table.Td>

                      <Table.Td>
                        {getFormattedTime(roleCast.startTime)}
                      </Table.Td>
                      <Table.Td>{getFormattedTime(roleCast.endTime)}</Table.Td>
                    </>
                  ) : null}
                </Table.Tr>
              ))}
            </React.Fragment>
          ))}
        </React.Fragment>
      ))}
    </>
  )
}
